// src/components/EmailConfiguration.jsx
//
// Configuration > Mail Configuration (SideMenu.jsx's new Configuration
// submenu). Matches CET-HTML-Code/mail-configurations.html: three tabs
// (Template / E-Mail Setting / Test).
//
// Template tab is full CRUD against Mail_Notification_Templates -- the
// SAME table app.py's get_mail_template()/send_templated_mail() read
// from when it sends real account-activation, access-request, and
// login-OTP emails (see mail_notification_templates.sql, which creates
// and seeds that table). Editing or disabling a template here changes
// what the next matching email actually sends.
//
// The theme's static mockup shows a decorative Bold/Italic/Underline/
// bullet/align/font-color toolbar above the body textarea, but none of
// that is wired to anything in the static HTML either -- this app
// doesn't load a rich-text editor library anywhere, and a row of
// buttons that look clickable but silently do nothing is exactly the
// kind of thing that reads as broken (see RateCard.jsx's View/Edit/
// Delete history). So the body field here is a plain "Body (HTML)"
// textarea plus a real "Preview" toggle that renders the current HTML
// below it -- less decoration, but everything shown actually works.
//
// E-Mail Setting tab is read-only by design (see the section comment
// in app.py above get_mail_settings()) -- it shows the SMTP connection
// this app is actually configured to send with, with no live "Save"
// since changing those credentials from a web form is a bigger call
// than this pass is scoped to.
//
// Test tab sends one real email through that same SMTP connection.
import React, { useCallback, useEffect, useState } from "react";
import { api as axios } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";
import TemplateFormFields, { emptyTemplateForm } from "./TemplateFormFields";

const API_BASE = API_ORIGIN;
const TABS = [
  { key: "template", label: "Template" },
  { key: "setting", label: "E-Mail Setting" },
  { key: "test", label: "Test" }
];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

export default function EmailConfiguration() {
  const [activeTab, setActiveTab] = useState("template");

  // ---- Template tab ----------------------------------------------------
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [page, setPage] = useState(1);

  // Creating a new template is its own full page now (TemplateCreatePage.jsx,
  // routed at /configuration/mail/templates/new) -- this modal is Edit/View
  // only, so editingTemplate is always set whenever modalOpen is true.
  // modalMode ("view" | "edit") picks which of the Action column's two
  // buttons opened it -- View renders the exact same modal/layout with
  // every field locked (TemplateFormFields' readOnly prop), same pattern
  // as RateCard.jsx's View modal.
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("edit");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState(emptyTemplateForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const loadTemplates = useCallback(() => {
    setLoadingTemplates(true);
    setLoadError("");
    axios
      .get(`${API_BASE}/mail-templates`)
      .then((res) => setTemplates(res.data?.data || []))
      .catch((err) => {
        console.error("Failed to load mail templates:", err);
        setLoadError(
          err?.response?.data?.error ||
            "Failed to load email templates. If this just started happening, run mail_notification_templates.sql against your database."
        );
      })
      .finally(() => setLoadingTemplates(false));
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const totalPages = Math.max(1, Math.ceil(templates.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const pagedTemplates = templates.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => {
    setPage(1);
  }, [rowsPerPage]);

  const allOnPageSelected = pagedTemplates.length > 0 && pagedTemplates.every((t) => selectedIds.includes(t.Template_ID));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pagedTemplates.some((t) => t.Template_ID === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pagedTemplates.map((t) => t.Template_ID)])));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const populateForm = (t) => {
    setEditingTemplate(t);
    setForm({
      Template_Code: t.Template_Code,
      Template_Name: t.Template_Name || "",
      Template_Description: t.Template_Description || "",
      Template_Type: t.Template_Type || "",
      Version: t.Version || "1.0",
      Recipient_To: t.Recipient_To || "User",
      Recipient_CC: t.Recipient_CC || "",
      Recipient_BCC: t.Recipient_BCC || "",
      Subject: t.Subject || "",
      Body_Html: t.Body_Html || "",
      Placeholders: t.Placeholders || "",
      Is_Active: t.Is_Active !== false
    });
    setFormError("");
    setShowPreview(false);
  };

  const openViewModal = (t) => {
    populateForm(t);
    setModalMode("view");
    setModalOpen(true);
  };

  const openEditModal = (t) => {
    populateForm(t);
    setModalMode("edit");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormError("");
  };

  const handleSaveTemplate = async () => {
    setFormError("");
    if (!form.Template_Name.trim()) {
      setFormError("Template Name is required.");
      return;
    }
    if (!form.Subject.trim()) {
      setFormError("Subject is required.");
      return;
    }
    if (!form.Body_Html.trim()) {
      setFormError("Body is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        Template_Name: form.Template_Name.trim(),
        Template_Description: form.Template_Description.trim(),
        Template_Type: form.Template_Type.trim(),
        Version: form.Version.trim() || "1.0",
        Recipient_To: form.Recipient_To,
        Recipient_CC: form.Recipient_CC.trim(),
        Recipient_BCC: form.Recipient_BCC.trim(),
        Subject: form.Subject.trim(),
        Body_Html: form.Body_Html,
        Placeholders: form.Placeholders.trim(),
        Is_Active: form.Is_Active
      };

      const res = await axios.put(`${API_BASE}/mail-templates/${editingTemplate.Template_ID}`, payload);

      const data = res?.data;
      if (!data || data.success !== true) {
        setFormError(data?.error || "Failed to save template.");
        setSaving(false);
        return;
      }

      setSaving(false);
      setModalOpen(false);
      loadTemplates();
    } catch (err) {
      console.error("Save mail template failed:", err);
      setFormError(err?.response?.data?.error || "Failed to save template.");
      setSaving(false);
    }
  };

  // ---- E-Mail Setting tab -----------------------------------------------
  const [mailSettings, setMailSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState("");

  useEffect(() => {
    if (activeTab !== "setting" || mailSettings) return;
    setSettingsLoading(true);
    setSettingsError("");
    axios
      .get(`${API_BASE}/mail-settings`)
      .then((res) => setMailSettings(res.data?.data || null))
      .catch((err) => setSettingsError(err?.response?.data?.error || "Failed to load mail settings."))
      .finally(() => setSettingsLoading(false));
  }, [activeTab, mailSettings]);

  // ---- Test tab -----------------------------------------------------------
  const [testEmail, setTestEmail] = useState("");
  const [testSubject, setTestSubject] = useState("");
  const [testBody, setTestBody] = useState("This is a test email from the Cost Estimation Portal's Email Configuration page.");
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState("");

  const handleSendTest = async () => {
    setTestError("");
    setTestResult(null);
    if (!testEmail.trim()) {
      setTestError("Email ID is required.");
      return;
    }
    setSendingTest(true);
    try {
      const res = await axios.post(`${API_BASE}/send-test-mail`, {
        Email: testEmail.trim(),
        Subject: testSubject.trim() || undefined,
        Body: testBody
      });
      if (!res?.data?.success) {
        setTestError(res?.data?.error || "Failed to send test email.");
      } else {
        setTestResult(res.data.message || "Test email sent.");
      }
    } catch (err) {
      setTestError(err?.response?.data?.error || "Failed to send test email.");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <>
      <div className="page-title-head d-flex align-items-center mb-3">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail ("Cost
              Estimation / {page}"), same pattern and color (#02bc9c,
              16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
              -- replaces the earlier #788CA5 bordered/uppercase title
              treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              Email Configuration
            </span>
          </nav>
        </div>
      </div>

      <ul className="nav nav-pills bg-light border p-1 mb-3 flex-nowrap flex-sm-wrap overflow-auto" role="tablist">
        {TABS.map((tab) => (
          <li className="nav-item flex-shrink-0" role="presentation" key={tab.key}>
            <button
              type="button"
              className={`nav-link fw-semibold px-3 px-md-4${activeTab === tab.key ? " active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ---- Template tab ---- */}
      {activeTab === "template" && (
        <div className="card border-0 shadow-sm">
          <div className="d-flex align-items-center gap-2 border-light card-header flex-wrap justify-content-between">
            <div className="d-flex gap-2 align-items-center">
              <h4 className="card-title m-0 fw-bold">Email Template</h4>
            </div>
            <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">
              <div className="w-100 w-sm-auto">
                <select className="form-select" value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                  <option value={5}>5</option>
                  <option value={8}>8</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>
          </div>

          {loadError && (
            <div className="card-body pb-0">
              <div className="alert alert-danger mb-0">{loadError}</div>
            </div>
          )}

          <div className="table-responsive">
            {/* email-config-table (new, scoped) -- per request, same
                treatment as RateCard.jsx/ProjectList.jsx/UsersList.jsx: row
                background #f2f2f2 @ 38% Transparency, hover #02bc9c @ 95%
                Transparency, via themeOverrides.css. Its own class rather
                than changing .table-custom/.table-hover directly, since
                those are shared by ManageRoles.jsx / RoleMatrix.jsx /
                AIAssistant.jsx and shouldn't change color there too. */}
            <table className="mb-0 table table-centered table-custom table-hover table-select email-config-table text-nowrap w-100">
              {/* Per request, the solid dark-green (bg-green) header fill
                  is removed -- header cells now use the same plain
                  bg-light look as the other tables (ManageRoles.jsx,
                  RoleMatrix.jsx, etc.) instead of a dark fill + text-light. */}
              <thead className="bg-light border-bottom border-top">
                <tr className="text-dark fw-bold">
                  <th className="ps-3" style={{ width: "1%" }}>
                    <input
                      className="form-check-input fs-14 mt-0"
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  {/* Description and Type columns removed per request --
                      the freed width is redistributed below (mostly to
                      Template Name, which was getting truncated) rather
                      than just left as blank space. */}
                  <th className="px-3 py-3" style={{ width: "30%" }}>Template Name</th>
                  <th className="px-3 py-3" style={{ width: "8%" }}>Version</th>
                  <th className="px-3 py-3" style={{ width: "14%" }}>Created By</th>
                  <th className="px-3 py-3" style={{ width: "15%" }}>Date Created</th>
                  <th className="px-3 py-3" style={{ width: "15%" }}>Date Modified</th>
                  <th className="px-3 py-3 text-center" style={{ width: "17%" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loadingTemplates ? (
                  <tr><td colSpan={7} className="text-center text-muted py-4">Loading...</td></tr>
                ) : pagedTemplates.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-muted py-4">No email templates found.</td></tr>
                ) : (
                  pagedTemplates.map((t) => (
                    <tr key={t.Template_ID}>
                      <td className="ps-3">
                        <input
                          className="form-check-input form-check-input-light fs-14"
                          type="checkbox"
                          checked={selectedIds.includes(t.Template_ID)}
                          onChange={() => toggleSelectOne(t.Template_ID)}
                        />
                      </td>
                      <td className="px-3 border-end">
                        {t.Template_Name}
                        {!t.Is_Active && <span className="badge bg-secondary-subtle text-secondary ms-2">Inactive</span>}
                      </td>
                      <td className="px-3 border-end">{t.Version || "—"}</td>
                      <td className="px-3 border-end">{t.Created_By || "—"}</td>
                      <td className="px-3 border-end">{formatDate(t.Created_At)}</td>
                      <td className="px-3 border-end">{formatDate(t.Updated_At)}</td>
                      <td className="px-3 text-center">
                        <div className="d-flex align-items-center gap-1 justify-content-center">
                          <button type="button" className="btn btn-default btn-icon btn-sm" title="View" onClick={() => openViewModal(t)}>
                            <i className="ti fs-lg ti-eye" />
                          </button>
                          <button type="button" className="btn btn-default btn-icon btn-sm" title="Edit" onClick={() => openEditModal(t)}>
                            <i className="ti fs-lg ti-pencil" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="border-0 card-footer">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="text-muted fs-13">
                {templates.length === 0
                  ? "Showing 0 of 0 templates"
                  : `Showing ${(currentPage - 1) * rowsPerPage + 1} to ${Math.min(currentPage * rowsPerPage, templates.length)} of ${templates.length} templates`}
              </div>
              {totalPages > 1 && (
                <ul className="pagination pagination-sm pagination-boxed mb-0 justify-content-center">
                  <li className={`page-item${currentPage === 1 ? " disabled" : ""}`}>
                    <button type="button" className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      <i className="ti ti-chevron-left" />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <li key={n} className={`page-item${currentPage === n ? " active" : ""}`}>
                      <button type="button" className="page-link" onClick={() => setPage(n)}>{n}</button>
                    </li>
                  ))}
                  <li className={`page-item${currentPage === totalPages ? " disabled" : ""}`}>
                    <button type="button" className="page-link" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                      <i className="ti ti-chevron-right" />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- E-Mail Setting tab (read-only, see app.py's get_mail_settings) ---- */}
      {activeTab === "setting" && (
        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-1">Email Outgoing Settings</h5>
            <p className="text-muted fs-14 mb-3">
              This is the SMTP connection your backend is actually configured to send mail with. To change it, edit
              the backend's <code>.env</code> file (EMAIL_HOST / EMAIL_PORT / EMAIL_USER / EMAIL_PASS) and restart
              the server -- changing live mail credentials from this page isn't enabled.
            </p>

            {settingsError && <div className="alert alert-danger">{settingsError}</div>}

            {settingsLoading ? (
              <div className="text-muted py-3">Loading...</div>
            ) : mailSettings ? (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Hostname</label>
                    <input type="text" className="form-control" value={mailSettings.Hostname || "—"} readOnly disabled />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Port</label>
                    <input type="text" className="form-control" value={mailSettings.Port || "—"} readOnly disabled />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Username</label>
                    <input type="text" className="form-control" value={mailSettings.Username || "—"} readOnly disabled />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Sender Name</label>
                    <input type="text" className="form-control" value={mailSettings.Sender || "—"} readOnly disabled />
                  </div>
                </div>
                <div className={`alert ${mailSettings.Configured ? "alert-success" : "alert-warning"} mb-0`}>
                  {mailSettings.Configured
                    ? "SMTP is fully configured -- account activation, access request, and login OTP emails can send."
                    : "SMTP isn't fully configured yet (EMAIL_HOST/EMAIL_USER/EMAIL_PASS missing from .env) -- emails will fail to send until it is."}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* ---- Test tab ---- */}
      {activeTab === "test" && (
        <div className="card border-0 shadow-sm">
          <div className="card-body p-2 p-md-4">
            <h5 className="fw-bold mb-4">Send a Test Mail</h5>

            {testError && <div className="alert alert-danger">{testError}</div>}
            {testResult && <div className="alert alert-success">{testResult}</div>}

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Email ID</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="abc@gmail.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Subject</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Subject of Mail"
                  value={testSubject}
                  onChange={(e) => setTestSubject(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold">Message</label>
              <textarea
                className="form-control"
                rows={6}
                value={testBody}
                onChange={(e) => setTestBody(e.target.value)}
              />
            </div>

            <div className="text-end">
              <button type="button" className="btn btn-primary px-4" onClick={handleSendTest} disabled={sendingTest}>
                {sendingTest ? "Sending..." : "Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- View / Edit Template modal ---- */}
      {modalOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold">{modalMode === "view" ? "View Template" : "Edit Template"}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                {formError && <div className="alert alert-danger py-2 px-3 mb-3">{formError}</div>}
                <TemplateFormFields
                  form={form}
                  setForm={setForm}
                  isEdit={true}
                  showPreview={showPreview}
                  setShowPreview={setShowPreview}
                  readOnly={modalMode === "view"}
                />
              </div>
              <div className="modal-footer border-0 pt-0">
                {modalMode === "view" ? (
                  <>
                    <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
                    <button type="button" className="btn btn-primary px-4" onClick={() => setModalMode("edit")}>Edit</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                    <button type="button" className="btn btn-primary px-4" onClick={handleSaveTemplate} disabled={saving}>
                      {saving ? "Saving..." : "Update"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
